import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, BrainCircuit, CheckCircle2, Loader2, Mail, MinusCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  conflict: { label: "CONFLICT", cls: "bg-[#FF3B30]/15 text-[#FF3B30] border-[#FF3B30]/30", Icon: AlertTriangle },
  aligned: { label: "ALIGNED", cls: "bg-[#00FFA3]/15 text-[#00FFA3] border-[#00FFA3]/30", Icon: CheckCircle2 },
  neutral: { label: "NEUTRAL", cls: "bg-white/5 text-gray-400 border-white/10", Icon: MinusCircle },
};

function HealthDigestFooter() {
  const { authHeaders } = useAuth();
  const [sending, setSending] = useState(false);

  const sendTest = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/portfolio-health/email-test`, {}, { headers: authHeaders() });
      if (data.status === "sent") toast.success(`Health digest sent to ${data.recipient}`);
      else if (data.status === "skipped") toast.info("Email service not configured yet — digest was skipped");
      else if (data.status === "empty") toast.info(data.message);
      else toast.error(data.reason || "Could not send the digest email");
    } catch {
      toast.error("Could not send the digest email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#00E0FF]/15 bg-[#00E0FF]/[0.04] px-4 py-3" data-testid="health-digest-cta">
      <div className="flex items-center gap-2.5 min-w-0">
        <Mail className="w-4 h-4 text-[#00E0FF] shrink-0" />
        <div className="min-w-0">
          <div className="text-xs font-bold text-white">Weekly Portfolio Health email</div>
          <div className="text-[10px] text-gray-500">
            This report lands in your inbox every Monday — conflicts, exit risks & take-profit calls ·{" "}
            <Link to="/settings" className="text-[#00E0FF] hover:underline" data-testid="manage-email-prefs-link">
              Manage email preferences
            </Link>
          </div>
        </div>
      </div>
      <Button
        data-testid="health-digest-send-btn"
        size="sm"
        variant="outline"
        disabled={sending}
        onClick={sendTest}
        className="border-[#00E0FF]/30 text-[#00E0FF] hover:bg-[#00E0FF]/10 hover:text-[#00E0FF] text-xs h-8"
      >
        {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Mail className="w-3.5 h-3.5 mr-1.5" />}
        Send me a test email
      </Button>
    </div>
  );
}

/** AI copilot for real holdings: flags positions that conflict with current AI signals. */
export default function HoldingsInsights() {
  const { authHeaders } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/brokers/portfolio-insights`, { headers: authHeaders() })
      .then((r) => setData(r.data))
      .catch(() => setData({ has_holdings: false }));
  }, [authHeaders]);

  if (!data?.has_holdings) return null;
  const s = data.summary;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8" data-testid="holdings-insights-section">
      <div className="flex items-center gap-2 mb-1">
        <BrainCircuit className="w-5 h-5 text-[#00E0FF]" />
        <h2 className="text-lg font-bold text-white" style={{ fontFamily: "Unbounded" }}>AI COPILOT · YOUR HOLDINGS</h2>
      </div>
      <p className="text-xs text-gray-500 mb-4">Live AI signals cross-referenced with the positions in your connected brokerage accounts</p>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <Card className="glass-effect border-[#FF3B30]/20">
          <CardContent className="p-4">
            <div className="text-2xl font-bold mono text-[#FF3B30]" data-testid="insights-conflicts-count">{s.conflicts}</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Conflicts</div>
            {s.value_at_risk > 0 && <div className="text-[10px] text-[#FF3B30]/80 mono">${s.value_at_risk.toLocaleString()} at risk</div>}
          </CardContent>
        </Card>
        <Card className="glass-effect border-[#00FFA3]/20">
          <CardContent className="p-4">
            <div className="text-2xl font-bold mono text-[#00FFA3]" data-testid="insights-aligned-count">{s.aligned}</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Aligned</div>
          </CardContent>
        </Card>
        <Card className="glass-effect">
          <CardContent className="p-4">
            <div className="text-2xl font-bold mono text-white">{s.neutral}</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Neutral</div>
          </CardContent>
        </Card>
      </div>

      <Card className="glass-effect">
        <CardContent className="p-4 space-y-2">
          {data.holdings.map((h) => {
            const meta = STATUS_META[h.status];
            return (
              <div key={h.symbol} className={`rounded-lg border px-3 py-2.5 ${h.status === "conflict" ? "border-[#FF3B30]/25 bg-[#FF3B30]/[0.04]" : "border-white/5 bg-white/[0.02]"}`} data-testid={`holding-insight-${h.symbol}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[9px] font-bold tracking-wider shrink-0 ${meta.cls}`}>
                      <meta.Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-white mono flex items-center gap-2 flex-wrap">
                        {h.symbol}
                        <span className="text-gray-500 font-normal text-xs">{h.units} sh · ${h.value.toLocaleString()}</span>
                        {h.pnl_percent != null && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${h.pnl_percent >= 0 ? "bg-[#00FFA3]/10 text-[#00FFA3]" : "bg-[#FF3B30]/10 text-[#FF3B30]"}`} data-testid={`holding-pnl-${h.symbol}`}>
                            {h.pnl_percent >= 0 ? "+" : ""}{h.pnl_percent}%
                          </span>
                        )}
                        {h.exit_risk && (
                          <span className="text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded bg-[#FF3B30] text-white animate-pulse" data-testid={`exit-risk-badge-${h.symbol}`}>
                            EXIT RISK
                          </span>
                        )}
                        {h.take_profit && (
                          <span className="text-[9px] font-bold tracking-widest px-1.5 py-0.5 rounded bg-[#FFB020]/20 text-[#FFB020] border border-[#FFB020]/40" data-testid={`take-profit-badge-${h.symbol}`}>
                            TAKE PROFIT
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-gray-600 truncate">{h.accounts.join(", ")}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`text-xs font-bold ${h.signal.includes("BUY") ? "text-[#00FFA3]" : h.signal.includes("SELL") ? "text-[#FF3B30]" : "text-gray-400"}`}>
                      {h.signal} · {h.conviction}
                    </div>
                    <div className="text-[10px] text-gray-500 mono">Win {h.win_probability}% · ${Number(h.current_price).toLocaleString()}</div>
                  </div>
                </div>
                {h.status === "conflict" && (
                  <p className="text-[11px] text-[#FF3B30]/90 mt-1.5 leading-snug">
                    {h.exit_risk
                      ? `${h.symbol} is down ${Math.abs(h.pnl_percent)}% since you bought and the AI signals ${h.signal} — consider your stop-loss (suggested $${Number(h.stop_loss).toLocaleString()}).`
                      : h.reasoning}
                  </p>
                )}
                {h.take_profit && h.status !== "conflict" && (
                  <p className="text-[11px] text-[#FFB020]/90 mt-1.5 leading-snug">
                    Up {h.pnl_percent}% since purchase and the signal is now {h.signal} — consider locking in gains.
                  </p>
                )}
              </div>
            );
          })}
          {data.untracked.length > 0 && (
            <p className="text-[10px] text-gray-600 pt-1">
              Not covered by AI signals: {data.untracked.join(", ")}
            </p>
          )}
        </CardContent>
      </Card>

      <HealthDigestFooter />
    </motion.div>
  );
}
